import { DecimalPipe } from '@angular/common'
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core'
import { RouterModule } from '@angular/router'

import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'

import { AiBadgeComponent } from '../../../../components/ai-badge/ai-badge.component'
import { LoadingComponent } from '../../../../components/loading/loading.component'
import {
  Option,
  SelectComponent,
} from '../../../../components/select/select.component'
import {
  Assessment,
  KeyActionEvaluation,
} from '../../../../services/assessment/assessment.mapping'
import { AdminOrg, User } from '../../../../services/user/user.mapping'
import { getMimeType } from '../../../../util/file/encoding'
import { canDownloadTranscript } from '../../../../util/format-transcript'
import { CustomLabelValueMap } from '../../../../services/custom-label/custom-label.service'
import { SpeakerLabelPipe } from '../../../../services/custom-label/speaker-label.pipe'
import { MarkdownPipe } from '../markdown.pipe'
import {
  computeCategoryScores,
  isHighlightEvaluation,
  isLowLightEvaluation,
  isSpecialTenantWithoutCategories,
} from '../assessment-evaluation.util'

/**
 * アセスメント詳細のモバイル専用表示コンポーネント（閲覧専用）
 * ファイルアップロード・削除・名前変更等の操作機能はPC版のみ提供
 */
@Component({
  selector: 'app-assessment-mobile',
  standalone: true,
  imports: [
    MatIcon,
    SelectComponent,
    LoadingComponent,
    AiBadgeComponent,
    DecimalPipe,
    RouterModule,
    SpeakerLabelPipe,
    MarkdownPipe,
  ],
  templateUrl: './assessment-mobile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './assessment-mobile.component.scss',
})
export class AssessmentMobileComponent implements OnChanges {
  @Input() user: User | null = null
  @Input() assessment: Assessment | null = null
  @Input() assessments: Option[] = []
  @Input() selectedAssessment: Option = { id: 'none', name: '商談未設定' }
  @Input() keyActionEvaluations: KeyActionEvaluation[] = []
  @Input() selectedKeyAction: KeyActionEvaluation | null = null
  @Input() showSummary = false
  @Input() overallScoreLabel = ''
  @Input() speakerLabelValues: CustomLabelValueMap = {}
  @Input() adminOrg: AdminOrg | null = null
  @Input() downloadUrl: string | null = null

  @Output() assessmentChange = new EventEmitter<{ id: string }>()
  @Output() keyActionSelect = new EventEmitter<string>()
  @Output() summaryToggle = new EventEmitter<void>()
  @Output() contentAccessed = new EventEmitter<string>()
  @Output() transcriptDownload = new EventEmitter<void>()

  /** カテゴリ別スコアのキャッシュ */
  categoryScores: { category: string; average: number }[] = []

  /** ハイライト判定キャッシュ（evaluation.id → boolean） */
  highlightMap: Map<string, boolean> = new Map()

  /** ロウライト判定キャッシュ（evaluation.id → boolean） */
  lowlightMap: Map<string, boolean> = new Map()

  /** スコアラベルキャッシュ（evaluation.id → label | null） */
  scoreLabelMap: Map<string, string | null> = new Map()

  constructor(private snackBar: MatSnackBar) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['keyActionEvaluations'] ||
      changes['user'] ||
      changes['adminOrg']
    ) {
      this.categoryScores = computeCategoryScores(
        this.keyActionEvaluations,
        this.user?.orgId,
      )
      this.rebuildEvaluationCache()
    }
  }

  /** ハイライトキャッシュを参照する */
  isHighlight(evaluation: KeyActionEvaluation): boolean {
    return this.highlightMap.get(evaluation.id) ?? false
  }

  /** ロウライトキャッシュを参照する */
  isLowLight(evaluation: KeyActionEvaluation): boolean {
    return this.lowlightMap.get(evaluation.id) ?? false
  }

  /** スコアラベルキャッシュを参照する */
  getScoreLabel(evaluation: KeyActionEvaluation): string | null {
    return this.scoreLabelMap.get(evaluation.id) ?? null
  }

  /** テキストをクリップボードにコピー */
  copyToClipboard(text: string | null): void {
    if (!text) return
    navigator.clipboard.writeText(text)
    this.snackBar.open('コピーしました', '閉じる', { duration: 2000 })
  }

  /** 音声ファイルのMIMEタイプを取得 */
  getAudioMimeType(url: string): string {
    const extension = url.split('?').shift()?.split('.')?.pop()
    if (!extension) {
      return 'audio/mpeg'
    }
    return getMimeType(extension)
  }

  /** 文字起こしテキストをダウンロード可能か判定する */
  canDownloadTranscript(): boolean {
    return canDownloadTranscript(this.assessment)
  }

  /** ハイライト/ロウライト/スコアラベルキャッシュを再構築する */
  private rebuildEvaluationCache(): void {
    const threshold = this.adminOrg?.scoreImprovementThreshold ?? 3
    const specialWithout = isSpecialTenantWithoutCategories(
      this.user?.orgId,
      this.categoryScores.length,
    )

    this.highlightMap = new Map()
    this.lowlightMap = new Map()
    this.scoreLabelMap = new Map()

    for (const evaluation of this.keyActionEvaluations) {
      const highlight = isHighlightEvaluation(
        evaluation,
        threshold,
        specialWithout,
      )
      const lowlight = isLowLightEvaluation(
        evaluation,
        threshold,
        specialWithout,
      )
      this.highlightMap.set(evaluation.id, highlight)
      this.lowlightMap.set(evaluation.id, lowlight)
      this.scoreLabelMap.set(
        evaluation.id,
        highlight ? '良かった点' : lowlight ? '改善したい点' : null,
      )
    }
  }
}
